import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  debounceTime,
  of,
  Subject,
  timer,
  debounce,
  withLatestFrom,
  map,
  Observable,
} from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import { PlEntityCollectionService } from '../../pl-entities';
import {
  PlFetchGrid,
  PlGridAction,
  PlGridView,
  PlGridColumn,
  PlRowAction,
} from '../models';

@Component({ template: '' })
export abstract class PlBaseGridComponent implements OnInit {
  protected readonly destroyRef = inject(DestroyRef);

  protected page: number;

  protected limit: number;

  view: PlGridView;

  rowActions: PlRowAction[];

  selectable: boolean;

  columns: PlGridColumn[] = [];

  columnsDefinition: string[];

  gridViews?: PlGridView[];

  ngOnInit() {
    this.buildColumns();
  }

  protected buildColumns() {
    let columns = this.columns.map((c) => c.key);
    if (this.rowActions && this.rowActions.length > 0) {
      columns.push('action');
    }
    if (this.selectable) {
      columns = ['select', ...columns];
    }
    this.columnsDefinition = [...columns];
  }
}

// , S extends IPlHttpService<T>

@Component({ template: '' })
export abstract class PlGridComponent<T>
  extends PlBaseGridComponent
  implements OnInit, OnDestroy
{
  data$: Observable<T[]> = this.store.entities$;

  count$ = this.store.selectTotalCount();

  // Prevents loading spinner from disappearing immediately
  loading$ = this.store.loading$?.pipe(
    withLatestFrom(this.store.loaded$),
    map(([loading, loaded]) => loading || !loaded),
    debounce((loading) => (loading ? of({}) : timer(500)))
  );

  fetch$: Subject<void> = new Subject();

  onQuery$: BehaviorSubject<PlFetchGrid> = new BehaviorSubject(null);

  params: { [key: string]: string };

  constructor(
    protected store: PlEntityCollectionService<T>,
    protected router: Router,
    protected route: ActivatedRoute
  ) {
    super();
  }

  override ngOnInit() {
    if (this.gridViews && this.gridViews.length > 0) {
      const dfv = this.gridViews.find((v) => v.default);
      if (dfv) {
        this.onViewChanged(dfv);
      }
    }
    this.fetch$
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.fetch());
    super.ngOnInit();
  }

  // This is required for child classes
  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngOnDestroy() {}

  fetch() {
    this.store.getWithQuery({
      ...this.params,
      page: `${this.page}`,
      limit: `${this.limit}`,
    });
  }

  onAction(action: PlGridAction<T, void>) {
    const { name, value } = action;
    let uuid;
    switch (name) {
      case 'edit':
        uuid = (<any>value).uuid;
        this.router.navigate([`../details/${uuid}`], {
          relativeTo: this.route,
        });
        break;
      case 'delete':
        this.store.delete(action.value).subscribe();
        break;
    }
  }

  setPage(event: PageEvent) {
    this.page = event.pageIndex;
    this.limit = event.pageSize;
    this.fetch$.next();
  }

  onViewChanged(view: PlGridView) {
    this.params = {
      ...this.params,
      is_active: view.key === 'active' ? '1' : '0',
    };
    this.fetch$.next();
  }
}
